import { RowData, Table } from '../../../../components/table/Table';
import { Pagination } from '../../../../components/table/Pagination';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getBookingsFilters,
  getBookingsPage,
  setPage,
  useTableFilteredBookings,
} from '../../../../state/bookings/bookingsFilterSlice';
import { useAppDispatch, useAppSelector } from '../../../../state/hooks';
import { TableColumn } from '../../../../components/table/TableRow';
import { VehicleRepresentation } from '../../../../components/dataRepresentation/VehicleRepresentation';
import { ServiceRepresentation } from '../../../../components/dataRepresentation/ServiceRepresentation';
import { CustomFormattedDate, useCustomIntl } from '../../../../i18n/i18n';
import { MonetaryValue } from '../../../../components/MonetaryValue';
import { BookingStatusLabel } from '../BookingStatusLabel';
import { ServiceType, VehicleType } from '../../../../codegen/vehicles';
import { BookingStatus } from '../../../../codegen/bookings';
import { Booking } from '../../../../models';
import {
  getSelectedBooking,
  selectBooking,
  unselectBooking,
} from '../../state/bookingsUiSlice';
import isEqual from 'lodash/isEqual';
import { useBookingUpdateState } from '../../../../state/bookings/bookingsUpdateSlice';

type BookingRow = {
  vehicleName?: string;
  vehicleType?: VehicleType;
  service: ServiceType;
  arrival: string;
  estimatedTotalPrice: number;
  currency: string;
  status: BookingStatus;
  driverName: string;
};

export const BookingsTable = () => {
  const intl = useCustomIntl();
  const dispatch = useAppDispatch();

  const { filteredBookings, totalBookings, isFetching } =
    useTableFilteredBookings();

  const selectedBooking = useAppSelector(getSelectedBooking);
  const { canceledBookingId } = useBookingUpdateState();

  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const accumulatedBookingsRef = useRef<Map<string, Booking>>(new Map());
  const page = useAppSelector(getBookingsPage);
  const filters = useAppSelector(getBookingsFilters, isEqual);

  useEffect(() => {
    setAllBookings([]);
    accumulatedBookingsRef.current.clear();
  }, [filters]);

  useEffect(() => {
    filteredBookings.forEach((b) =>
      accumulatedBookingsRef.current.set(b.id, b),
    );

    setAllBookings(Array.from(accumulatedBookingsRef.current.values()));
  }, [filteredBookings]);

  useEffect(() => {
    if (canceledBookingId) {
      const updatedBookings = allBookings.map((booking) =>
        booking.id === canceledBookingId
          ? {
              ...booking,
              status: BookingStatus.Canceled,
            }
          : booking,
      );

      // updates the bookings list with the new status
      setAllBookings(updatedBookings);

      // updates the sidebar
      setSelectedBooking(updatedBookings, canceledBookingId);
    }
  }, [canceledBookingId]);

  const setSelectedBooking = (
    fromBookingsList: Booking[],
    bookingId: string,
  ) => {
    const booking = fromBookingsList.find((b) => b.id === bookingId);
    if (booking) {
      dispatch(selectBooking(booking));
    }
  };

  const handleBookingClicked = (id: string) => {
    if (selectedBooking?.id === id) {
      dispatch(unselectBooking());
    } else {
      setSelectedBooking(allBookings, id);
    }
  };

  const handlePageChanged = () => {
    dispatch(setPage(page + 1));
  };

  const columns: TableColumn<BookingRow>[] = [
    {
      id: 'vehicleName',
      label: intl.formatMessage({
        id: 'bookings.bookingsTable.header.vehicle',
      }),
      renderer: (booking: BookingRow) => (
        <VehicleRepresentation
          vehicleType={booking.vehicleType}
          vehicleName={booking.vehicleName}
        />
      ),
    },
    {
      id: 'service',
      label: intl.formatMessage({
        id: 'bookings.bookingsTable.header.service',
      }),
      renderer: (booking: BookingRow) => (
        <ServiceRepresentation serviceType={booking.service} />
      ),
    },
    {
      id: 'arrival',
      label: intl.formatMessage({
        id: 'bookings.bookingsTable.header.arrival',
      }),
      renderer: (booking: BookingRow) => (
        <CustomFormattedDate value={booking.arrival} />
      ),
    },
    {
      id: 'estimatedTotalPrice',
      label: intl.formatMessage({
        id: 'bookings.bookingsTable.header.estimatedTotalPrice',
      }),
      renderer: (booking: BookingRow) => (
        <span className="text-medium">
          {!!booking.estimatedTotalPrice && !!booking.currency && (
            <MonetaryValue
              amount={booking.estimatedTotalPrice}
              currency={booking.currency}
            />
          )}
        </span>
      ),
    },
    {
      id: 'status',
      label: intl.formatMessage({
        id: 'bookings.bookingsTable.header.status',
      }),
      renderer: (booking: BookingRow) => (
        <BookingStatusLabel status={booking.status} />
      ),
    },
    {
      id: 'driverName',
      label: intl.formatMessage({
        id: 'bookings.bookingsTable.header.driverName',
      }),
      renderer: (booking: BookingRow) => <>{booking.driverName}</>,
    },
  ];

  const rowData: RowData<BookingRow>[] = useMemo(
    () =>
      allBookings.map((b: Booking) => ({
        key: b.id,
        vehicleName: b.vehicle?.name,
        vehicleType: b.vehicle?.type,
        vehicleGroups: b.vehicle?.groups.map((g) => g.name) ?? [],
        service: b.serviceType,
        arrival: b.arrivalTime,
        estimatedTotalPrice: parseFloat(b.estimatedPrice.value),
        currency: b.estimatedPrice.currency,
        status: b.status,
        driverName: b.driverName,
      })),
    [allBookings],
  );

  return (
    <>
      <div data-testid="bookings-body-bookings-table">
        <Table
          columns={columns}
          rowData={rowData}
          highlightedRow={selectedBooking?.id}
          onRowClicked={(key) => {
            handleBookingClicked(key);
          }}
          isLoading={isFetching}
        />
      </div>
      {allBookings.length > 0 && (
        <Pagination
          loadedItems={allBookings.length}
          totalItems={totalBookings}
          onLoadMore={handlePageChanged}
          isLoading={isFetching}
        />
      )}
    </>
  );
};
